import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Monitor, Play, Phone, Check } from "lucide-react";
import { getCourseBySlug, formatCZK, SPOLUUCAST_PCT } from "@/data/courses";
import { company, videoPoster } from "@/data/company";
import { Seo } from "@/components/Seo";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const course = getCourseBySlug(slug);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlaying(false);
  }, [slug]);

  if (!course) {
    return (
      <div data-testid="course-not-found" className="pt-52 pb-32 text-center px-6 bg-gavito-light">
        <h1 className="font-heading font-black text-4xl text-gavito-navy">Kurz nenalezen</h1>
        <button onClick={() => navigate("/kurzy")} className="mt-6 inline-flex items-center gap-2 text-gavito-green font-semibold">
          <ArrowLeft className="w-4 h-4" /> Zpět na kurzy
        </button>
      </div>
    );
  }

  const spoluucast = course.spoluucast ?? (course.cena ? Math.round(course.cena * SPOLUUCAST_PCT) : null);

  const play = () => {
    setPlaying(true);
    videoRef.current?.play();
  };

  return (
    <div data-testid="course-detail-page" className="bg-gavito-light">
      <Seo title={course.title} description={course.kratkyPopis} path={`/kurzy/${course.slug}`} />
      <section className="pt-36 md:pt-44 pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link to="/kurzy" data-testid="back-to-courses" className="inline-flex items-center gap-2 text-sm font-semibold text-gavito-navy/60 hover:text-gavito-green transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Zpět na kurzy
          </Link>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {course.dotovany && (
                <span className="bg-gavito-green/12 text-gavito-green text-xs font-bold px-3 py-1.5 rounded-full">Lze hradit s Úřadem práce</span>
              )}
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gavito-navy/60"><Monitor className="w-4 h-4" />{course.forma}</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gavito-navy/60"><Clock className="w-4 h-4" />{course.delka}</span>
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter leading-[0.98] text-gavito-navy">
              {course.title}
            </h1>
            <p className="mt-6 text-lg md:text-xl font-medium text-gavito-navy/70 leading-relaxed">{course.kratkyPopis}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {/* VIDEO – pouze u kurzů s příspěvkem ÚP */}
            {course.dotovany && (course.videoEmbed ? (
              <>
                <div className="relative rounded-3xl overflow-hidden bg-gavito-dark border border-gavito-navy/10" style={{ paddingTop: `${course.videoAspect || 56.25}%` }}>
                  <iframe
                    data-testid="course-video-embed"
                    src={course.videoEmbed}
                    title={`Videoukázka kurzu ${course.title}`}
                    loading="lazy"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
                <p className="text-sm text-gavito-navy/50 mt-3">Videoukázka je dostupná zdarma a bez přihlášení či registrace.</p>
              </>
            ) : course.videoUrl ? (
              <>
                <div className="relative rounded-3xl overflow-hidden bg-gavito-dark border border-gavito-navy/10">
                  <video
                    ref={videoRef}
                    data-testid="course-video"
                    controls={playing}
                    preload="metadata"
                    poster={videoPoster}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    className="w-full aspect-video block"
                  >
                    <source src={course.videoUrl} type="video/mp4" />
                    Váš prohlížeč nepodporuje přehrávání videa.
                  </video>
                  {!playing && (
                    <button
                      data-testid="video-play-button"
                      onClick={play}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-gavito-navy/50 group"
                    >
                      <span className="w-20 h-20 md:w-24 md:h-24 bg-gavito-green rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 md:w-10 md:h-10 text-gavito-navy fill-gavito-navy ml-1" />
                      </span>
                      <span className="mt-5 text-white font-semibold tracking-wide text-sm">Přehrát videoukázku kurzu</span>
                    </button>
                  )}
                </div>
                <p className="text-sm text-gavito-navy/50 mt-3">Videoukázka je dostupná zdarma a bez přihlášení či registrace.</p>
              </>
            ) : (
              <div data-testid="video-placeholder" className="relative rounded-3xl overflow-hidden border border-gavito-navy/10 bg-gavito-navy">
                <img src={videoPoster} alt="" className="w-full aspect-video object-cover opacity-30" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gavito-green/90 flex items-center justify-center">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-gavito-navy fill-gavito-navy ml-1" />
                  </span>
                  <p className="mt-5 text-white font-heading font-bold text-lg md:text-xl">Videoukázka kurzu se připravuje</p>
                  <p className="text-white/70 text-sm mt-1">Brzy zde bude k dispozici – zdarma a bez přihlášení.</p>
                </div>
              </div>
            ))}

            {/* POPIS */}
            <div className="mt-12">
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-gavito-navy mb-5">O kurzu</h2>
              <p className="text-gavito-navy/75 leading-relaxed text-lg">{course.popis}</p>
            </div>

            {/* CO SE NAUCITE */}
            <div className="mt-12">
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-gavito-navy mb-6">Co se naučíte</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {course.naucite.map((item) => (
                  <div key={item} className="flex items-start gap-3 bg-white border border-gavito-navy/10 rounded-2xl p-5">
                    <span className="w-6 h-6 rounded-full bg-gavito-green/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-gavito-green" />
                    </span>
                    <span className="text-gavito-navy font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {course.osnova && (
              <div className="mt-12">
                <h2 className="font-heading font-extrabold text-3xl tracking-tight text-gavito-navy mb-6">Osnova kurzu</h2>
                <div className="space-y-4">
                  {course.osnova.map((blok, bi) => (
                    <div key={bi} className="bg-white border border-gavito-navy/10 rounded-2xl p-6">
                      <div className="flex items-start gap-3">
                        <span className="font-heading font-black text-gavito-green text-lg leading-none mt-0.5">{String(bi + 1).padStart(2, "0")}</span>
                        <div className="flex-1">
                          <h3 className="font-heading font-bold text-lg text-gavito-navy">{blok.skupina}</h3>
                          <ul className="mt-3 space-y-2">
                            {blok.body.map((b) => (
                              <li key={b} className="flex items-start gap-2 text-gavito-navy/70">
                                <span className="w-1.5 h-1.5 rounded-full bg-gavito-green mt-2 shrink-0" />
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 bg-gavito-navy text-white rounded-3xl p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gavito-green font-bold mb-2">Pro koho je kurz určen</p>
              <p className="text-lg text-white/80 leading-relaxed">{course.urcenoPro}</p>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white border border-gavito-navy/10 rounded-3xl p-8 shadow-[0_20px_50px_-30px_rgba(27,42,78,0.3)]">
              {course.cena ? (
                <>
                  <p className="text-sm text-gavito-navy/50">Cena kurzu</p>
                  <p className="font-heading font-black text-4xl tracking-tighter text-gavito-navy mt-1">{formatCZK(course.cena)}</p>
                  <p className="text-xs text-gavito-navy/50 mt-1">vč. DPH</p>
                  {course.dotovany && (
                    <div className="mt-4 bg-gavito-green/10 rounded-2xl p-4 space-y-1.5">
                      <p className="text-sm font-semibold text-gavito-navy">Kurz s příspěvkem Úřadu práce</p>
                      {course.prispevekUP && (
                        <p className="text-sm text-gavito-navy/70">
                          Příspěvek ÚP: <span className="font-bold text-gavito-green">{formatCZK(course.prispevekUP)}</span>
                        </p>
                      )}
                      <p className="text-sm text-gavito-navy/70">
                        Spoluúčast účastníka: <span className="font-bold text-gavito-green">{formatCZK(spoluucast)}</span> (min. 23,27 % ceny).
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm text-gavito-navy/50">Cena</p>
                  <p className="font-heading font-black text-3xl tracking-tighter text-gavito-navy mt-1">Na vyžádání</p>
                  <p className="text-sm text-gavito-navy/60 mt-2">Připravíme nezávaznou nabídku na míru.</p>
                </>
              )}

              <div className="mt-6 space-y-0 text-sm">
                {[
                  ["Forma výuky", course.forma],
                  ["Rozsah", course.delka],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-3 border-b border-gavito-navy/10">
                    <span className="text-gavito-navy/60">{k}</span>
                    <span className="font-bold text-gavito-navy">{v}</span>
                  </div>
                ))}
              </div>

              <Link to="/kontakt" data-testid="course-enroll-cta" className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-gavito-green text-white font-semibold px-6 py-4 rounded-full hover:bg-gavito-navy transition-colors">
                Mám zájem <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${company.phone}`} data-testid="course-call-cta" className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-gavito-navy/20 text-gavito-navy font-semibold px-6 py-3.5 rounded-full hover:bg-gavito-navy hover:text-white transition-colors">
                <Phone className="w-4 h-4" /> {company.phone}
              </a>
              <p className="text-xs text-gavito-navy/50 text-center mt-4">Veškerou administrativu vyřídíme za vás.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
